// Trust Suppression Management Component
import AjaxUtils from "../../util/ajax_utils";

/**
 * Trust Suppression Management Component
 *
 * Manages suppression lists for thirdparties and domains.
 * Suppressed hostnames are excluded from Trust Monitor processing.
 */
export const ApckTrustSuppressionManagement = {
    data: function() {
        return {
            loading: false,
            submitting: false,

            // Filter state
            typeFilter: '', // '', 'thirdparty', or 'domain'

            // Suppression data
            suppressionItems: [],
            totalCount: 0,
            currentPage: 1,
            totalPages: 0,
            suppressionEnabled: true,

            // Add modal state
            addModal: {
                render: false,
                open: false,
                hostname: '',
                suppressionType: 'thirdparty',
                matchSubdomains: true,
                notes: '',
                removeExisting: true
            },

            // Autocomplete state
            hostnameSuggestions: [],
            showSuggestions: false,
            searchLoading: false,
            activeIndex: -1,
            searchDebounceTimer: null,

            // Table columns
            cols: [
                {
                    data: 'hostname',
                    heading: 'Hostname',
                    canSearch: true,
                    width: '25%'
                },
                {
                    data: 'suppression_type',
                    heading: 'Type',
                    width: '10%',
                    center: true
                },
                {
                    data: 'apex_domain',
                    heading: 'Apex Domain',
                    width: '15%'
                },
                {
                    data: 'match_subdomains',
                    heading: 'Match Subdomains',
                    ctype: 'boolcolumn',
                    width: '12%',
                    center: true
                },
                {
                    data: 'hit_count',
                    heading: 'Hits',
                    width: '8%',
                    center: true
                },
                {
                    data: 'suppressed_by',
                    heading: 'Suppressed By',
                    width: '15%'
                },
                {
                    data: 'suppressed_at',
                    heading: 'Suppressed',
                    ctype: 'datecolumn',
                    width: '15%'
                }
            ],

            // Table actions
            actions: [
                {
                    title: 'Remove',
                    icon: 'fa-trash',
                    class: 'btn-danger',
                    handler: this.removeSuppression,
                    useOwnCol: true,
                    hideLabel: true
                }
            ]
        };
    },
    computed: {
        tableSrc() {
            let url = '/trust/suppression/list';
            if (this.typeFilter) {
                url += '?type=' + this.typeFilter;
            }
            return url;
        },
        typeOptions() {
            return [
                { value: '', label: 'All Types' },
                { value: 'thirdparty', label: 'Thirdparties' },
                { value: 'domain', label: 'Domains' }
            ];
        },
        suppressionTypeOptions() {
            return [
                { value: 'thirdparty', label: 'Thirdparty - Suppress external thirdparties' },
                { value: 'domain', label: 'Domain - Suppress monitored domains' }
            ];
        },
        suppressionTypeLabel() {
            const option = this.suppressionTypeOptions.find(opt => opt.value === this.addModal.suppressionType);
            return option ? option.label : 'Suppression Type';
        },
        canSubmit() {
            return this.addModal.hostname.trim().length > 0;
        }
    },
    methods: {
        async loadSuppression() {
            const cmp = this;
            cmp.loading = true;

            let url = `/trust/suppression/list?page=${cmp.currentPage}&limit=50`;
            if (cmp.typeFilter) {
                url += `&type=${cmp.typeFilter}`;
            }

            try {
                await AjaxUtils.ajax_get({
                    url: url,
                    success: function(data) {
                        if (data.success) {
                            cmp.suppressionItems = data.items || [];
                            cmp.totalCount = data.results || 0;
                            cmp.totalPages = data.pages || 0;
                            cmp.suppressionEnabled = data.suppression_enabled !== false;
                        } else {
                            cmp.$flash({ message: data.message || 'Failed to load suppression list', type: 'error' });
                        }
                    },
                    fail: function() {
                        cmp.$flash({ message: 'Failed to load suppression list', type: 'error' });
                    },
                    opts: {
                        dataType: 'json',
                        flash_form_errors: false,
                        flash_404_errors: false,
                        show_success_flash: false
                    }
                });
            } catch (e) {
                // Promise rejection already handled by fail callback
            }

            cmp.loading = false;
        },

        openAddModal() {
            this.addModal.hostname = '';
            this.addModal.suppressionType = 'thirdparty';
            this.addModal.matchSubdomains = true;
            this.addModal.notes = '';
            this.addModal.removeExisting = true;
            // Reset autocomplete state
            this.hostnameSuggestions = [];
            this.showSuggestions = false;
            this.searchLoading = false;
            this.activeIndex = -1;
            this.addModal.render = true;
            this.$nextTick(() => {
                this.addModal.open = true;
            });
        },

        closeAddModal() {
            this.addModal.open = false;
            // Clear autocomplete
            this.hostnameSuggestions = [];
            this.showSuggestions = false;
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }
            this.$nextTick(() => {
                this.addModal.render = false;
            });
        },

        async submitSuppression() {
            const cmp = this;

            if (!cmp.canSubmit) {
                cmp.$flash({ message: 'Please enter a hostname', type: 'warning' });
                return;
            }

            cmp.submitting = true;

            try {
                await AjaxUtils.ajax_post({
                    url: '/trust/suppression/add',
                    data: JSON.stringify({
                        hostname: cmp.addModal.hostname.trim().toLowerCase(),
                        suppression_type: cmp.addModal.suppressionType,
                        match_subdomains: cmp.addModal.matchSubdomains,
                        notes: cmp.addModal.notes,
                        remove_existing: cmp.addModal.removeExisting
                    }),
                    success: function(data) {
                        cmp.submitting = false;
                        if (data.success) {
                            cmp.$flash({ message: data.message, type: 'success' });
                            cmp.closeAddModal();
                            cmp.reloadTable();
                        } else {
                            cmp.$flash({ message: data.message, type: 'error' });
                        }
                    },
                    fail: function() {
                        cmp.submitting = false;
                        cmp.$flash({ message: 'Failed to add suppression', type: 'error' });
                    },
                    opts: {
                        dataType: 'json',
                        contentType: 'application/json',
                        flash_form_errors: false,
                        flash_404_errors: false
                    }
                });
            } catch (e) {
                cmp.submitting = false;
            }
        },

        removeSuppression(row) {
            const cmp = this;
            const suppressionId = row.get('_id');
            const hostname = row.get('hostname');
            const suppressionType = row.get('suppression_type');

            cmp.$modal.confirm(
                {
                    message: `Are you sure you want to remove "${hostname}" from the ${suppressionType} suppression list?`,
                    title: 'Remove Suppression',
                    onConfirm: async () => {
                        await AjaxUtils.ajax_get({
                            url: `/trust/suppression/remove/${suppressionId}`,
                            success: function(data) {
                                if (data.success) {
                                    cmp.$flash({ message: data.message, type: 'success' });
                                    cmp.reloadTable()
                                } else {
                                    cmp.$flash({ message: data.message, type: 'error' })
                                }
                            },
                            fail: function() {
                                cmp.$flash({ message: 'Failed to remove suppression', type: 'error' })
                            },
                            opts: {
                                dataType: 'json',
                                flash_form_errors: false,
                                flash_404_errors: false,
                            },
                        })
                    }
                }
            );
        },

        setTypeFilter(value) {
            this.typeFilter = value;
            this.reloadTable();
        },

        reloadTable() {
            if (this.$refs.suppressionTable) {
                this.$refs.suppressionTable.store.reload();
            }
            this.loadSuppression();
        },

        // Autocomplete methods
        onHostnameInput(value) {
            this.addModal.hostname = value;
            this.activeIndex = -1;

            // Debounce the search
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            const query = value.trim();
            if (query.length < 2) {
                this.hostnameSuggestions = [];
                this.showSuggestions = false;
                return;
            }

            this.searchDebounceTimer = setTimeout(() => {
                this.searchHostnames(query);
            }, 300);
        },

        async searchHostnames(query) {
            const cmp = this;
            cmp.searchLoading = true;

            try {
                await AjaxUtils.ajax_get({
                    url: `/trust/suppression/search?q=${encodeURIComponent(query)}&type=${cmp.addModal.suppressionType}&limit=10`,
                    success: function(data) {
                        cmp.searchLoading = false;
                        if (data.success && data.results) {
                            cmp.hostnameSuggestions = data.results;
                            cmp.showSuggestions = cmp.hostnameSuggestions.length > 0;
                        } else {
                            cmp.hostnameSuggestions = [];
                            cmp.showSuggestions = false;
                        }
                    },
                    fail: function() {
                        cmp.searchLoading = false;
                        cmp.hostnameSuggestions = [];
                        cmp.showSuggestions = false;
                    },
                    opts: {
                        dataType: 'json',
                        flash_form_errors: false,
                        flash_404_errors: false,
                        show_success_flash: false
                    }
                });
            } catch (e) {
                cmp.searchLoading = false;
            }
        },

        onHostnameKeydown(event) {
            if (!this.showSuggestions || this.hostnameSuggestions.length === 0) {
                if (event.key === 'ArrowDown' && this.addModal.hostname.trim().length >= 2) {
                    // Re-show suggestions if we have a query
                    if (this.hostnameSuggestions.length > 0) {
                        this.showSuggestions = true;
                    }
                }
                return;
            }

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.activeIndex = Math.min(this.activeIndex + 1, this.hostnameSuggestions.length - 1);
                    this.scrollActiveIntoView();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.activeIndex = Math.max(this.activeIndex - 1, -1);
                    this.scrollActiveIntoView();
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.activeIndex >= 0 && this.activeIndex < this.hostnameSuggestions.length) {
                        this.selectSuggestion(this.hostnameSuggestions[this.activeIndex]);
                    }
                    break;
                case 'Escape':
                    this.showSuggestions = false;
                    this.activeIndex = -1;
                    break;
            }
        },

        scrollActiveIntoView() {
            this.$nextTick(() => {
                const ref = this.$refs['suggestion-' + this.activeIndex];
                const el = Array.isArray(ref) ? ref[0] : ref;
                if (el) {
                    el.scrollIntoView({ block: 'nearest' });
                }
            });
        },

        onHostnameFocus() {
            if (this.hostnameSuggestions.length > 0) {
                this.showSuggestions = true;
            }
        },

        onHostnameBlur() {
            // Delay hiding to allow click on suggestion
            setTimeout(() => {
                this.showSuggestions = false;
                this.activeIndex = -1;
            }, 200);
        },

        selectSuggestion(suggestion) {
            this.addModal.hostname = suggestion.hostname;
            this.showSuggestions = false;
            this.hostnameSuggestions = [];
            this.activeIndex = -1;
        },

        onSuppressionTypeChange(value) {
            this.addModal.suppressionType = value;
            // Clear hostname and suggestions when type changes
            this.addModal.hostname = '';
            this.hostnameSuggestions = [];
            this.showSuggestions = false;
            this.activeIndex = -1;
        }
    },
    mounted() {
        this.loadSuppression();
    },
    template: `
        <ui-page title="Trust Monitor Suppression Lists" stickyTitle>
            <template #title-bar-buttons>
                <ui-button
                    label="Add Suppression"
                    iconLeft="fa-plus"
                    @click="openAddModal"
                />
            </template>
            
            <template #content>
                <ui-layout direction="column" gap="4">
                    <!-- Info panel -->
                    <ui-panel-base v-if="!suppressionEnabled" color="yellow">
                        <ui-layout align="center" gap="4">
                            <ui-icon icon="fa-exclamation-triangle" type="warning" />
                            <span>
                                Suppression is currently <strong>disabled</strong> in Trust Monitor settings.
                                Suppression rules will not be checked during processing.
                            </span>
                        </ui-layout>
                    </ui-panel-base>
                    
                    <ui-panel-base>
                        <ui-layout align="center" gap="4">
                            <ui-icon icon="fa-info-circle" type="info" />
                            <span>
                                Suppressed hostnames are excluded from Trust Monitor. Apex domains can match all subdomains.
                                Hit counts show how many times each rule has been matched during processing.
                            </span>
                        </ui-layout>
                    </ui-panel-base>
                    
                    <!-- Filters -->
                    <ui-layout gap="4" align="center">
                        <span class="text--secondary" v-if="totalCount > 0">
                            {{ totalCount }} suppression rule{{ totalCount !== 1 ? 's' : '' }}
                        </span>
                    </ui-layout>
                    
                    <!-- Empty state -->
                    <ui-layout 
                        v-if="!loading && suppressionItems.length === 0" 
                        direction="column" 
                        align="center" 
                        justify="center" 
                        class="dashboard-scope-message py-16"
                    >
                        <ui-panel-base class="text-center" style="max-width: 600px;">
                            <ui-layout direction="column" align="center" justify="center" gap="4" class="py-8">
                                <ui-icon icon="fa-filter-circle-xmark" size="lg" type="secondary" />
                                <ui-heading tag="h2">No Suppression Rules</ui-heading>
                                <p class="text--secondary">
                                    Suppression rules allow you to exclude specific hostnames from 
                                    Trust Monitor processing.
                                </p>
                                <p class="text--secondary">
                                    When a hostname is suppressed, it will no longer appear in your 
                                    thirdparty or domain lists and will be skipped during data imports.
                                </p>
                                <ui-button
                                    label="Add Your First Suppression Rule"
                                    iconLeft="fa-plus"
                                    @click="openAddModal"
                                />
                            </ui-layout>
                        </ui-panel-base>
                    </ui-layout>
                    
                    <!-- Suppression table -->
                    <apck-table
                        v-if="!loading && suppressionItems.length > 0"
                        ref="suppressionTable"
                        :columns="cols"
                        :actions="actions"
                        :src="tableSrc"
                        :limit="50"
                        :border="false"
                        :show-toolbar="false"
                        newStyleTable
                    />
                    
                    <ui-spinner :show="loading" theme="dark" />
                </ui-layout>
                
                <!-- Add Modal -->
                <ui-modal
                    v-if="addModal.render"
                    title="Add Suppression Rule"
                    size="md"
                    align="center"
                    :open="addModal.open"
                    @close="closeAddModal"
                    class="trust-suppression-management-modal"
                >
                    <template #content>
                        <ui-layout direction="column" gap="4">
                            <div class="autocomplete-wrapper" style="position: relative;">
                                <ui-input-text
                                    label="Hostname"
                                    placeholder="Search thirdparties..."
                                    :modelValue="addModal.hostname"
                                    @update:modelValue="onHostnameInput"
                                    @keydown.native="onHostnameKeydown"
                                    @focus.native="onHostnameFocus"
                                    @blur.native="onHostnameBlur"
                                    autoComplete="off"
                                    required
                                />
                                <div 
                                    v-if="showSuggestions && hostnameSuggestions.length > 0" 
                                    class="autocomplete-suggestions"
                                    ref="suggestionsList"
                                >
                                    <div
                                        v-for="(suggestion, index) in hostnameSuggestions"
                                        :key="suggestion.hostname"
                                        :ref="'suggestion-' + index"
                                        @mousedown.prevent="selectSuggestion(suggestion)"
                                        class="autocomplete-suggestion-item"
                                        :class="{ 'active': index === activeIndex }"
                                        @mouseenter="activeIndex = index"
                                    >
                                        {{ suggestion.hostname }}
                                    </div>
                                </div>
                                <div v-if="searchLoading" style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%);">
                                    <ui-spinner size="sm" :show="true" />
                                </div>
                            </div>
                            <p class="text--secondary text--sm" style="margin-top: -8px;">
                                Type at least 2 characters to search existing thirdparties
                            </p>
                            
                            <ui-input-checkbox
                                label="Match subdomains"
                                description="When enabled, suppressing example.com will also match www.example.com, api.example.com, etc."
                                :modelValue="addModal.matchSubdomains"
                                @update:modelValue="addModal.matchSubdomains = $event"
                            />
                            
                            <ui-input-checkbox
                                label="Remove existing data"
                                description="Delete existing Trust Monitor data for this hostname"
                                :modelValue="addModal.removeExisting"
                                @update:modelValue="addModal.removeExisting = $event"
                            />
                            
                            <ui-input-text
                                label="Notes (optional)"
                                placeholder="Reason for suppression"
                                :modelValue="addModal.notes"
                                @update:modelValue="addModal.notes = $event"
                            />
                        </ui-layout>
                    </template>
                    
                    <template #footer>
                        <ui-button
                            label="Cancel"
                            type="outline"
                            @click="closeAddModal"
                        />
                        <ui-button
                            label="Add Suppression"
                            :disabled="!canSubmit || submitting"
                            :loading="submitting"
                            @click="submitSuppression"
                        />
                    </template>
                </ui-modal>
            </template>
        </ui-page>
    `
};

/**
 * Quick Suppress Button Component
 *
 * A button component that can be used in thirdparty lists/details to quickly
 * add a thirdparty to the suppression list.
 */
export const ApckQuickSuppressButton = {
    props: {
        hostname: {
            type: String,
            required: true
        },
        suppressionType: {
            type: String,
            default: 'thirdparty'
        },
        buttonType: {
            type: String,
            default: 'outline'
        },
        buttonColor: {
            type: String,
            default: 'yellow'
        },
        size: {
            type: String,
            default: 'sm'
        },
        hideLabel: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            loading: false,
            modalOpen: false,
            modalRender: false,
            matchSubdomains: true,
            removeExisting: true,
            notes: ''
        };
    },
    methods: {
        openModal() {
            this.matchSubdomains = true;
            this.removeExisting = true;
            this.notes = '';
            this.modalRender = true;
            this.$nextTick(() => {
                this.modalOpen = true;
            });
        },

        closeModal() {
            this.modalOpen = false;
            this.$nextTick(() => {
                this.modalRender = false;
            });
        },

        async submitSuppression() {
            const cmp = this;
            cmp.loading = true;

            try {
                await AjaxUtils.ajax_post({
                    url: '/trust/suppression/add',
                    data: JSON.stringify({
                        hostname: cmp.hostname.trim().toLowerCase(),
                        suppression_type: cmp.suppressionType,
                        match_subdomains: cmp.matchSubdomains,
                        notes: cmp.notes,
                        remove_existing: cmp.removeExisting
                    }),
                    success: function(data) {
                        cmp.loading = false;
                        if (data.success) {
                            cmp.$flash({ message: data.message, type: 'success' });
                            cmp.closeModal();
                            cmp.$emit('suppressed', { hostname: cmp.hostname });
                        } else {
                            cmp.$flash({ message: data.message, type: 'error' });
                        }
                    },
                    fail: function() {
                        cmp.loading = false;
                        cmp.$flash({ message: 'Failed to add suppression', type: 'error' });
                    },
                    opts: {
                        dataType: 'json',
                        contentType: 'application/json',
                        flash_form_errors: false,
                        flash_404_errors: false
                    }
                });
            } catch (e) {
                cmp.loading = false;
            }
        }
    },
    template: `
        <div class="quick-suppress-button">
            <ui-button
                :label="hideLabel ? '' : 'Suppress'"
                iconLeft="fa-ban"
                :type="buttonType"
                :color="buttonColor"
                :size="size"
                :title="'Suppress ' + hostname"
                :hideLabel="hideLabel"
                @click.prevent="openModal"
            />
            
            <ui-modal
                v-if="modalRender"
                :title="'Suppress ' + hostname"
                size="sm"
                align="center"
                :open.sync="modalOpen"
                @close="closeModal"
            >
                <template #content>
                    <ui-layout direction="column" gap="4">
                        <p>Add <strong>{{ hostname }}</strong> to the {{ suppressionType }} suppression list?</p>
                        
                        <ui-input-checkbox
                            label="Match subdomains"
                            :checked="matchSubdomains"
                            @update:checked="v => matchSubdomains = v"
                        />
                        
                        <ui-input-checkbox
                            label="Remove existing data"
                            :checked="removeExisting"
                            @update:checked="v => removeExisting = v"
                        />
                        
                        <ui-input-text
                            label="Notes (optional)"
                            placeholder="Reason for suppression"
                            :modelValue="notes"
                            @update:modelValue="v => notes = v"
                        />
                    </ui-layout>
                </template>
                
                <template #footer>
                    <ui-button
                        label="Cancel"
                        type="outline"
                        @click="closeModal"
                    />
                    <ui-button
                        label="Suppress"
                        color="yellow"
                        :disabled="loading"
                        :loading="loading"
                        @click="submitSuppression"
                    />
                </template>
            </ui-modal>
        </div>
    `
};

/**
 * Convert to Domain Button Component
 *
 * A button component for converting a thirdparty to a monitored domain.
 */
export const ApckConvertToDomainButton = {
    props: {
        thirdpartyId: {
            type: String,
            required: true
        },
        hostname: {
            type: String,
            required: true
        },
        buttonType: {
            type: String,
            default: 'outline'
        },
        buttonColor: {
            type: String,
            default: 'blue'
        },
        size: {
            type: String,
            default: 'sm'
        },
        hideLabel: {
            type: Boolean,
            default: false
        }
    },
    data: function() {
        return {
            loading: false,
            modalOpen: false,
            modalRender: false,
            previewData: null,
            addToSuppression: true,
            removeThirdpartyData: true,
            notes: 'Converted to monitored domain'
        };
    },
    computed: {
        hasPreview() {
            return this.previewData && this.previewData.success;
        }
    },
    methods: {
        async openModal() {
            this.loading = true;
            this.previewData = null;

            // Fetch preview data
            const cmp = this;
            try {
                await AjaxUtils.ajax_get({
                    url: `/trust/thirdparty/${cmp.thirdpartyId}/convert/preview`,
                    success: function(data) {
                        cmp.previewData = data;
                    },
                    fail: function() {
                        cmp.$flash({ message: 'Failed to load conversion preview', type: 'error' });
                    },
                    opts: {
                        dataType: 'json',
                        flash_form_errors: false,
                        flash_404_errors: false
                    }
                });
            } catch (e) {
                // Handled by fail callback
            }

            this.loading = false;

            if (this.previewData && this.previewData.success) {
                this.modalRender = true;
                this.$nextTick(() => {
                    this.modalOpen = true;
                });
            }
        },

        closeModal() {
            this.modalOpen = false;
            this.$nextTick(() => {
                this.modalRender = false;
            });
        },

        async submitConversion() {
            const cmp = this;
            cmp.loading = true;

            try {
                await AjaxUtils.ajax_post({
                    url: `/trust/thirdparty/${cmp.thirdpartyId}/convert`,
                    data: JSON.stringify({
                        add_to_suppression: cmp.addToSuppression,
                        remove_thirdparty_data: cmp.removeThirdpartyData,
                        notes: cmp.notes
                    }),
                    success: function(data) {
                        cmp.loading = false;
                        if (data.success) {
                            cmp.$flash({ message: data.message, type: 'success' });
                            cmp.closeModal();
                            // Redirect to new domain view
                            if (data.redirect_url) {
                                window.location.href = data.redirect_url;
                            }
                        } else {
                            cmp.$flash({ message: data.message, type: 'error' });
                        }
                    },
                    fail: function() {
                        cmp.loading = false;
                        cmp.$flash({ message: 'Failed to convert thirdparty', type: 'error' });
                    },
                    opts: {
                        dataType: 'json',
                        contentType: 'application/json',
                        flash_form_errors: false,
                        flash_404_errors: false
                    }
                });
            } catch (e) {
                cmp.loading = false;
            }
        }
    },
    template: `
        <div class="convert-to-domain-button">
            <ui-button
                :label="hideLabel ? '' : 'Convert to Domain'"
                iconLeft="fa-exchange-alt"
                :type="buttonType"
                :color="buttonColor"
                :size="size"
                :title="'Convert ' + hostname + ' to a monitored domain'"
                :hideLabel="hideLabel"
                :loading="loading && !modalRender"
                @click.prevent="openModal"
            />
            
            <ui-modal
                v-if="modalRender"
                :title="'Convert ' + hostname + ' to Domain'"
                size="md"
                align="center"
                :open.sync="modalOpen"
                @close="closeModal"
            >
                <template #content>
                    <ui-layout v-if="hasPreview" direction="column" gap="4">
                        <p>
                            This will convert the thirdparty <strong>{{ hostname }}</strong> 
                            to a monitored domain in Trust Monitor.
                        </p>
                        
                        <ui-panel-base color="light-slate">
                            <ui-layout direction="column" gap="2">
                                <ui-layout gap="2">
                                    <span class="text--secondary">Apex Domain:</span>
                                    <strong>{{ previewData.apex_domain }}</strong>
                                    <ui-badge v-if="previewData.scope_exists" label="Already in Scope" color="blue" />
                                </ui-layout>
                                
                                <ui-layout gap="2">
                                    <span class="text--secondary">Domain Record:</span>
                                    <ui-badge v-if="previewData.domain_exists" label="Already Exists" color="blue" />
                                    <ui-badge v-else label="Will be Created" color="green" />
                                </ui-layout>
                                
                                <ui-layout gap="2">
                                    <span class="text--secondary">Relationships:</span>
                                    <span>{{ previewData.relationships_count }} relationship(s)</span>
                                </ui-layout>
                                
                                <ui-layout gap="2">
                                    <span class="text--secondary">Vulnerabilities:</span>
                                    <span>{{ previewData.vulnerabilities_count }} vulnerability(ies)</span>
                                </ui-layout>
                            </ui-layout>
                        </ui-panel-base>
                        
                        <ui-layout v-if="previewData.warnings && previewData.warnings.length" direction="column" gap="2">
                            <ui-layout v-for="warning in previewData.warnings" :key="warning" align="center" gap="2">
                                <ui-icon icon="fa-exclamation-triangle" type="warning" />
                                <span class="text--secondary">{{ warning }}</span>
                            </ui-layout>
                        </ui-layout>
                        
                        <ui-input-checkbox
                            label="Add thirdparty to suppression list"
                            description="Prevents this hostname from appearing as a thirdparty in future scans"
                            :checked="addToSuppression"
                            @update:checked="v => addToSuppression = v"
                        />
                        
                        <ui-input-checkbox
                            label="Remove thirdparty data"
                            description="Delete existing thirdparty relationships, instances, and vulnerabilities"
                            :checked="removeThirdpartyData"
                            @update:checked="v => removeThirdpartyData = v"
                        />
                    </ui-layout>
                    
                    <ui-layout v-else align="center" justify="center">
                        <ui-spinner :show="true" theme="dark" />
                    </ui-layout>
                </template>
                
                <template #footer>
                    <ui-button
                        label="Cancel"
                        type="outline"
                        @click="closeModal"
                    />
                    <ui-button
                        label="Convert to Domain"
                        :disabled="loading || !hasPreview"
                        :loading="loading"
                        @click="submitConversion"
                    />
                </template>
            </ui-modal>
        </div>
    `
};
